import {
  hasuraRequest,
  getShape,
  getPropertiesWithMeta,
  fetchEntity,
  fetchCubeData,
} from "datastory-shared/utils/hasura"
import { gql } from "datastory-shared/utils/graphql"
import { xor } from "lodash"

import colors from "config/colors"

const KEY_SEPARATOR = "---"

const getOrderQuery = async () => {
  const orderQuery = gql`
    query Ordering {
      ordering: observation(
        where: {
          og_cube: {
            _eq: "og:og:politicalParty_og:valforskSurveyIssue_og:year"
          }
          og_valforskSurveyIssue: {
            _eq: "og:som-var-placerar-du-dig-pa-en-vanster-hogerskala"
          }
        }
      ) {
        politicalParty: og_politicalParty
        valforsk_issue: og_valforskSurveyIssue
        year: og_year
        value: og_value
      }
    }
  `
  const { ordering } = await hasuraRequest(orderQuery)

  return ordering
}

export const fetchData = async language => {
  const indicators = []
  const keyDimensionsMeta = {}

  const fetchCube = async cube => {
    const requestedKeyDimensions = cube.og_keyDimension.map(
      ({ object }) => object
    )
    const keyDimensions = keyDimensionsMeta
    const missingKeyDimensions = xor(keyDimensions, requestedKeyDimensions)
    const properties = missingKeyDimensions.map(id => ({ id }))

    const response = await getPropertiesWithMeta(properties)

    for (let i = 0; i < missingKeyDimensions.length; i++) {
      const key = missingKeyDimensions[i]
      const propertyMeta = response?.find(d => d.id === key)
      const valueClass = propertyMeta?.value_class?.[0]

      keyDimensionsMeta[key] = { ...propertyMeta }

      // fetch meta data for entity related to
      // dimension key (e.g. country === Country)
      if (valueClass) {
        const classMeta = await fetchEntity({
          id: valueClass.object,
          language,
        })
        const classShape = await getShape({
          instanceOf: valueClass.object,
        })
        keyDimensionsMeta[key] = {
          ...keyDimensionsMeta[key],
          ...valueClass,
          classMeta,
          classShape,
        }
      }
    }

    const dimensions = {}
    Object.keys(keyDimensionsMeta).forEach(k => {
      if (requestedKeyDimensions.includes(k))
        dimensions[k] = keyDimensionsMeta[k]
    })

    return fetchCubeData({
      cubeId: cube.entity_meta.id,
      measureId: "og:pollsterValuePercent",
      timeDimension: "og_date",
      dimensions,
      language,
    })
  }

  const [ordering, entities] = await Promise.all([
    getOrderQuery(),
    fetchEntity({
      id: "og:pollsterValuePercent",
      language,
      properties: [
        { id: "og:name" },
        {
          id: "og:measureDimensionOf", //The Cube
          properties: [
            {
              id: "og:keyDimension",
              properties: [{ id: "og:name" }, { id: "og:dataType" }],
            },
          ],
        },
      ],
    }),
  ])

  // Exlcude date_politicalParty_pollster cube
  const cubes = entities.og_measureDimensionOf.filter(
    i => i.entity_meta.id !== "og:og:date_og:politicalParty_og:pollster"
  )

  const allData = {}

  let preparedData = await Promise.all(cubes.map(cube => fetchCube(cube)))

  preparedData = preparedData.map((data, index) => ({
    data,
    id: cubes[index].entity_meta.id,
    dimensions: cubes[index]?.og_keyDimension.filter(
      d => d.object !== "og:date" && d.object !== "og:politicalParty"
    ),
  }))

  preparedData.forEach(({ data, dimensions, id }) => {
    const dates = new Set(data.map(({ og_date }) => og_date))
    const multiDimensionsKeys = dimensions.map(({ object }) => ({
      [object]: [...new Set(data.map(i => i[object.replace(":", "_")]))],
    }))

    allData[id] = {}

    dates.forEach(date => {
      const utcDate = +new Date(date)
      data
        .filter(i => i.og_date === date)
        .forEach(i => {
          const obj = {
            ...i,
            percentage: i.og_value,
            value: i.og_value,
            politicalParty: i.og_politicalParty,
            date: i.og_date,
            // TODO replace colors constants with colors from CMS
            color: colors.parties[i.og_politicalParty] || null,
            order:
              ordering.find(o => o.politicalParty === i.og_politicalParty)
                ?.value || null,
          }

          const key = multiDimensionsKeys
            .map(v => Object.keys(v)[0])
            .reduce((previousValue, currentValue) => {
              let str = previousValue
              if (str) str += KEY_SEPARATOR
              return str + obj[currentValue.replace(":", "_")]
            }, "")

          if (!allData[id][key]) allData[id][key] = {}
          if (!allData[id][key][utcDate]) allData[id][key][utcDate] = []

          allData[id][key][utcDate].push(obj)
        })
    })
  })

  const mainData = {}
  const dimensionsName = {}

  entities.og_measureDimensionOf.forEach(({ og_keyDimension }) => {
    og_keyDimension.forEach(kd => {
      if (!dimensionsName[kd.object]) {
        dimensionsName[kd.object] = kd.og_name[0]?.i18n
      }
    })
  })

  Object.keys(allData).map(k => {
    Object.keys(allData[k]).map(ik => {
      const newKey = `${k}_${ik}`
      mainData[newKey] = allData[k][ik]

      const firstDataItemByEntries = Object.entries(
        allData[k][ik][Object.keys(allData[k][ik])[0]][0]
      )

      const category = ik
        .split(KEY_SEPARATOR)
        .map(i => firstDataItemByEntries?.find(f => f.includes(i))?.[0])
        .reduce((p, c) => {
          const name = dimensionsName[c.replace("_", ":")]

          if (!p) return name
          return p + " & " + name
        }, "")

      const name = firstDataItemByEntries?.find(i => i.includes("name"))?.[1]
      const date = firstDataItemByEntries?.find(i => i.includes("og_date"))?.[1]
      const party = firstDataItemByEntries?.find(i =>
        i.includes("entity_by_og_politicalParty")
      )?.[1]?.name[0]?.i18n

      const label = name
        .split(", ")
        .filter(i => i !== date && i !== party)
        .join(" ")

      indicators.push({
        category,
        value: newKey,
        label,
      })
    })
  })

  indicators
    .sort((a, b) =>
      a.category.toUpperCase().localeCompare(b.category.toUpperCase())
    )
    .sort((a, b) => a.label.toUpperCase().localeCompare(b.label.toUpperCase()))

  return {
    mainData,
    indicators,
  }
}
